import React from 'react';
import { Download, Reply, Forward, Star, Trash2, Archive, ExternalLink } from 'lucide-react';
import { Email } from '../../../types/email';
import { formatBytes, formatDate } from '../../../utils/formatters';

interface EmailViewProps {
  email: Email;
  onClose: () => void;
  onReply: (email: Email) => void;
  onForward: (email: Email) => void;
  onDelete: (emailId: string) => void;
  onStar: (emailId: string) => void;
  onArchive: (emailId: string) => void;
  onViewDealDetails: (dealId: string) => void;
}

export default function EmailView({
  email,
  onClose,
  onReply,
  onForward,
  onDelete,
  onStar,
  onArchive,
  onViewDealDetails
}: EmailViewProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{email.subject}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onStar(email.id)}
              className={`p-2 rounded-full hover:bg-gray-100 ${
                email.isStarred ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              <Star className="w-5 h-5" />
            </button>
            <button
              onClick={() => onArchive(email.id)}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-400"
            >
              <Archive className="w-5 h-5" />
            </button>
            <button
              onClick={() => onDelete(email.id)}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-400"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-400"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{email.from}</p>
              <p className="text-sm text-gray-500">
                To: {email.to.join(', ')}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              {formatDate(email.timestamp)}
            </p>
          </div>

          {email.labels && email.labels.length > 0 && (
            <div className="mt-2 flex items-center space-x-2">
              {email.labels.map((label) => (
                <span
                  key={label}
                  className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          <div className="prose max-w-none">
            <pre className="whitespace-pre-wrap font-sans">{email.content}</pre>
          </div>

          {email.attachments && email.attachments.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Attachments
              </h4>
              <div className="space-y-2">
                {email.attachments.map((attachment) => (
                  <div
                    key={attachment.name}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <Download className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {attachment.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatBytes(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <button 
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <div className="flex space-x-2">
            <button
              onClick={() => onReply(email)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Reply className="w-4 h-4 inline-block mr-2" />
              Reply
            </button>
            <button
              onClick={() => onForward(email)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Forward className="w-4 h-4 inline-block mr-2" />
              Forward
            </button>
          </div>
          
          {email.dealId && (
            <button 
              onClick={() => onViewDealDetails(email.dealId!)}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
            >
              View Deal Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}